<template>
  <div class="explore-tokens md-layout md-alignment-center-center md-layout-item md-size-80 md-xsmall-size-100">
    <template v-if="!tokens.length">
      <div class="app__no-data-msg container container_30">
        <md-card class="md-layout-item
                      md-size-100">
          <md-card-content>
            <md-icon class="md-size-4x">toll</md-icon>
            <h2>{{ i18n.tr_no_assets() }}</h2>
            <p>{{ i18n.tr_no_assets_exp() }}</p>
          </md-card-content>
        </md-card>
      </div>
    </template>

    <md-card v-else class="explore-tokens__card container">

      <md-card-header>
        <div class="md-title">{{ i18n.lbl_explore_tokens() }}</div>
      </md-card-header>

      <div class="explore-tokens__inner">
        <div class="explore-tokens__list">
          <template v-if="tokens.length">
            <md-content class="md-scrollbar scrollbar">
              <md-list>
                <template v-for="(token, i) in tokens">
                  <md-list-item @click="selectToken(token)">

                    <md-avatar class="md-avatar-icon"
                               :class="`${hasBalance(token) ? 'md-primary' : 'md-accent'}`"
                    >
                      <template v-if="token.logoUrl">
                        <img :src="token.logoUrl" :alt="avatar(token.code)">
                      </template>

                      <template v-else>{{ avatar(token.code) }}</template>

                    </md-avatar>

                    <div class="explore-tokens__token-name"
                         :class="{ 'selected': selected.code === token.code }"
                    >
                        <span class="text_medium">{{ token.code }}
                          <md-icon class="explore-tokens__balance-exists-icon md-icon--half-sized"
                                   v-if="hasBalance(token)">check_circle</md-icon>
                        </span>
                      <span class="text_medium">{{ token.name }}</span>
                    </div>
                  </md-list-item>
                </template>
              </md-list>
            </md-content>
          </template>
        </div>

        <template v-if="selected">
          <md-card class="explore-tokens__details details">
            <div class="details__header">
              <md-avatar class="md-avatar-icon"
                         :class="`${hasBalance(selected) ? 'md-primary' : 'md-accent'}`"
              >
                <template v-if="selected.logoUrl">
                  <img :src="selected.logoUrl" :alt="avatar(selected.code)">
                </template>

                <template v-else>{{ avatar(selected.code) }}</template>

              </md-avatar>
              <div class="details__main-info text_medium">
                <span>{{ selected.code }}</span>
                <span>{{ selected.name }}</span>
                <span class="explore-tokens__balance-exists-msg" v-if="hasBalance(selected)">
                      {{ i18n.lbl_balance_exists()  }}
                      <md-icon class="explore-tokens__balance-exists-icon md-icon--half-sized">check_circle</md-icon>
                    </span>
              </div>
            </div>

            <div class="details__info">
              <div class="text_medium text_bold">{{ i18n.lbl_token_details() }}:</div>

              <div class="detail-row text_medium">
                <span>{{ i18n.lbl_code() }}</span>
                <span>{{ selected.code }}</span>
              </div>

              <div class="detail-row text_medium">
                <span>{{ i18n.lbl_available() }}</span>
                <span>{{ roundCurrency(selected.available, selected.code) }}</span>
              </div>

              <div class="detail-row text_medium">
                <span>{{ i18n.lbl_name() }}</span>
                <span>{{ selected.name }}</span>
              </div>

              <div class="detail-row text_medium">
                <span>{{ i18n.lbl_maximum() }}</span>
                <span>{{ roundCurrency(selected.max, selected.code) }}</span>
              </div>

              <div class="detail-row text_medium">
                <span>{{ i18n.lbl_issued() }}</span>
                <span>{{ roundCurrency(selected.issued, selected.code) }}</span>
              </div>

              <div v-if="selected.termsUrl" class="detail-row text_medium">
                <span>{{ i18n.lbl_terms() }}</span>
                <span>{{ selected.termsUrl }}</span>
              </div>
            </div>

            <md-card-actions class="explore-tokens__token-actions">
              <md-button v-if="!hasBalance(selected)"
                         class="md-primary btn"
                         @click="createBalance"
                         :disabled="isPending"
              >{{ i18n.lbl_add_to_balances() }}
              </md-button>
              <md-button v-else
                         class="md-primary btn"
                         @click="goHistory"
              >{{ i18n.lbl_view_history() }}
              </md-button>
            </md-card-actions>
          </md-card>
        </template>
      </div>

    </md-card>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex'
  import { accountsService } from '@api/accounts.service'
  import { EventDispatcher } from '@js/events/event_dispatcher'
  import { ErrorHandler } from '@js/errors/error_handler'
  import { vuexTypes } from '@vuex/types'
  import { confirmAction } from '@js/modals/confirmation_message'
  import { i18n } from '@js/i18n'

  import Detail from '../common/Detail.Row'
  import SubmitterMixin from '@com/mixins/submitter.mixin'
  import CurrencyRound from '@com/mixins/currency.round.mixin'

  export default {
    name: 'TokensExplore',
    mixins: [SubmitterMixin, CurrencyRound],
    components: { Detail },
    data: _ => ({
      selected: null,
      i18n
    }),
    async created () {
      this.selectToken(this.tokens[0])
      await Promise.all([
        this.loadTokens(),
        this.loadBalances()
      ])
    },
    computed: {
      ...mapGetters([
        vuexTypes.accountBalances,
        vuexTypes.tokens
      ])
    },
    methods: {
      ...mapActions({
        loadTokens: vuexTypes.GET_ALL_TOKENS,
        loadBalances: vuexTypes.GET_ACCOUNT_BALANCES
      }),
      selectToken (token) {
        this.selected = token || null
      },
      hasBalance (token) {
        if (!token) return false
        return Object.keys(this.accountBalances).includes(token.code)
      },
      avatar (code) {
        return code.length <= 2 ? code : code.charAt(0)
      },
      async createBalance () {
        if (!await confirmAction()) return
        const code = this.selected.code
        this.disable()
        try {
          await accountsService.createBalance(code)
          await this.loadBalances()
          EventDispatcher.dispatchShowSuccessEvent(i18n.cm_balance_created({ code }))
        } catch (e) {
          ErrorHandler.processUnexpected(e)
        }
        this.enable()
      },
      goHistory () {
        this.$router.push({ name: 'history.index', params: { tokenCode: this.selected.code } })
      }
    },
    watch: {
      tokens () {
        this.selectToken(this.tokens[0])
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/variables';
  @import '~scss/mixins';

  .explore-tokens {
    margin: auto;
    overflow: hidden;

    &__card {
      width: 100%;
      padding-bottom: 10px;
    }

    &__inner {
      display: flex;
      width: 100%;
      & .md-card-content {
        @include respond-to(medium-tablet) {
          padding: 0 !important;
        }
      }
    }

    &__list {
      flex: 0 0 25%;

      & .md-content {
        max-height: 70vh;
        overflow: auto;
      }
    }

    &__details {
      flex: 1 1 75%;
      display: flex;
      flex-direction: column;
      padding: 16px 32px;
      box-shadow: none;

      @include respond-to(small) {
        padding-right: 16px;
      }

      .details {

        &__header {
          display: flex;
        }

        &__main-info {
          margin: 0 12px;
        }

        &__info {
          margin: 24px 0;
        }
      }

      .detail-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 16px 0;
      }
    }

    &__token-name,
    .details__main-info {
      flex: 1 1 auto;
      display: flex;
      flex-direction: column;
    }
  }

  .explore-tokens__balance-exists-msg {
    color: $col-success;
  }


  .explore-tokens__balance-exists-icon {
    color: $col-success !important;
    margin-left: .15rem;
    position: relative;
    bottom: .1rem;
  }

  .selected {
    span {
      color: $col-md-primary;
    }
  }

</style>



// WEBPACK FOOTER //
// src/vue/app/tokens/Tokens.Explore.vue